import React from "react";
import { fileUrl } from "../lib/api";

/**
 * Avatar3D — uploaded 3D mannequin body PNG with the guest's cropped selfie
 * placed as a circular face where the figure's head is. A coloured "gown"
 * halo behind the figure indicates the role costume.
 *
 * Five pose variants live in `/public/avatars/poseN.png`. Each pose has
 * its own canvas size and head bounding box (measured offline).
 */
const POSES = {
  pose1: { w: 116, h: 225, head: { left: "26.7%", top: "3.0%", width: "63.0%", height: "31.7%" } },
  pose2: { w: 116, h: 225, head: { left: "10.3%", top: "3.0%", width: "63.0%", height: "31.7%" } },
  pose3: { w: 140, h: 237, head: { left: "34.9%", top: "5.8%", width: "52.2%", height: "30.1%" } },
  pose4: { w: 140, h: 237, head: { left: "12.8%", top: "5.8%", width: "52.2%", height: "30.1%" } },
  pose5: { w: 162, h: 245, head: { left: "41.3%", top: "8.1%", width: "44.6%", height: "29.5%" } },
};

export default function Avatar3D({ guest, costume, size = 100, pose = "pose1", label = true }) {
  const cfg = POSES[pose] || POSES.pose1;
  const figureSrc = `/avatars/${pose in POSES ? pose : "pose1"}.png`;
  const faceSrc = fileUrl(guest.character_path || guest.selfie_path);
  // container aspect = cfg.h / cfg.w
  const w = size;
  const h = Math.round(size * (cfg.h / cfg.w));

  return (
    <div
      className="flex flex-col items-center select-none pointer-events-none"
      style={{ transform: "translate(-50%, -50%)" }}
    >
      <div className="relative" style={{ width: w, height: h }}>
        {/* Coloured "gown" halo behind the figure for role colour */}
        <div
          className="absolute rounded-[40%] blur-md"
          style={{
            left: "8%", right: "8%",
            top: "40%", bottom: "4%",
            background: `linear-gradient(180deg, ${costume.gown} 0%, ${costume.gown}aa 100%)`,
            opacity: 0.85,
            boxShadow: `0 8px 18px ${costume.gown}66`,
          }}
          aria-hidden
        />
        {/* 3D figure body PNG */}
        <img
          src={figureSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
          draggable={false}
          style={{ filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.35))" }}
        />
        {/* Crown / role icon */}
        <div
          className="absolute drop-shadow-md"
          style={{
            left: "50%", top: "-12%",
            transform: "translateX(-50%)",
            fontSize: w * 0.32,
            lineHeight: 1,
          }}
        >
          {costume.icon}
        </div>
        {/* Selfie face circle — placed exactly over the figure's head */}
        <div
          className="absolute overflow-hidden rounded-full ring-4 shadow-xl z-10"
          style={{
            ...cfg.head,
            borderColor: costume.accent,
            boxShadow: `0 0 0 2px #fff, 0 0 14px ${costume.accent}aa`,
            background: "#fff",
          }}
        >
          <img
            src={faceSrc}
            alt={guest.name}
            className="w-full h-full object-cover"
            loading="lazy"
            draggable={false}
          />
        </div>
      </div>
      {label && (
        <div className="glass rounded-full px-2.5 py-0.5 mt-1 shadow-md">
          <p
            className="font-script text-sm text-plum leading-none whitespace-nowrap"
            style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}
          >
            {guest.name}
          </p>
        </div>
      )}
    </div>
  );
}

// Helper: pick a deterministic pose for a guest based on id hash
const POSE_KEYS = ["pose1", "pose2", "pose3", "pose4", "pose5"];
export function pickPose(guestId) {
  let h = 0;
  for (let i = 0; i < (guestId || "").length; i++) {
    h = (h * 31 + guestId.charCodeAt(i)) >>> 0;
  }
  return POSE_KEYS[h % POSE_KEYS.length];
}
